import dynamic from "next/dynamic";

import { FC, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import useCart from "modules/Cart/useCart";

import { selectAuthModalOpen, setAuthModalOpen } from "redux/slices/metamask";

import Header from "components/Header";
import SubHeader from "components/SubHeader";
import Footer from "components/Footer";
import AuthModal from "components/AuthModal";
import ScrollToTop from "components/ScrollToTop";

import { HEADER_HEIGHT_MOBILE, HEADER_HEIGHT_DESKTOP } from "utils/styles";

const ConfirmationModal = dynamic(
  () => import("modules/Cart/ConfirmationModal"),
  { ssr: false },
);

type LayoutProps = {
  isEmbedded?: boolean;
  paddingTop?: number;
  children: JSX.Element | (JSX.Element | null)[] | null;
  subHeader?: {
    header?: string;
    description?: string;
    modifiedBackground?: boolean;
    background?: string;
    isBackgroundGradient?: boolean;
  } | null;
  headerDisclaimer?: ReactNode;
  isCustomClient?: boolean;
  showFooter?: boolean;
  logo?: string;
  customFooterSocials?: JSX.Element | JSX.Element[];
  isHeaderAlwaysActive?: boolean;
  bgColor?: string;
  bgImage?: string;
  menuButtonColor?: string;
};

const Layout: FC<LayoutProps> = ({
  children,
  subHeader,
  paddingTop,
  isEmbedded,
  headerDisclaimer,
  isCustomClient,
  logo,
  customFooterSocials,
  isHeaderAlwaysActive = false,
  showFooter = true,
  bgColor,
  bgImage,
  menuButtonColor,
}) => {
  const theme = useTheme();
  const { isCartModalOpen, closeModal } = useCart();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const dispatch = useDispatch();
  const isAuthModalOpen = useSelector(selectAuthModalOpen);

  const closeAuthModal = () => {
    dispatch(setAuthModalOpen(false));
  };

  const HEADER_HEIGHT = isMobile ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP;

  return (
    <>
      <Header
        isEmbedded={isEmbedded}
        headerDisclaimer={headerDisclaimer}
        isCustomClient={isCustomClient}
        logo={logo}
        isAlwaysActive={isHeaderAlwaysActive}
        headerBgColor={bgColor}
        headerBgImage={bgImage}
        headerMenuButtonColor={menuButtonColor}
      />
      {subHeader && (
        <SubHeader
          header={subHeader?.header}
          subheader={subHeader?.description}
          modifiedBackground={subHeader?.modifiedBackground}
          background={subHeader?.background}
          isBackgroundGradient={subHeader?.isBackgroundGradient}
        />
      )}
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          minHeight: subHeader
            ? ""
            : isEmbedded
            ? "calc(100vh - 75px)"
            : "calc(100vh - 360px)",
          pt: {
            xs: subHeader?.background ? 0 : HEADER_HEIGHT / 8,
            sm:
              paddingTop ?? subHeader
                ? HEADER_HEIGHT / 8
                : HEADER_HEIGHT / 8 + 4,
          },
        }}
        pb={!isEmbedded ? 10 : 1}
      >
        <Container
          sx={{
            flex: 1,
            position: "relative",
          }}
        >
          {children}
        </Container>
      </Box>
      {isCartModalOpen && (
        <ConfirmationModal isOpen={isCartModalOpen} onClose={closeModal} />
      )}
      {isAuthModalOpen && (
        <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
      )}
      <ScrollToTop />
      {showFooter && (
        <Footer
          isEmbedded={isEmbedded}
          customFooterSocials={customFooterSocials}
        />
      )}
    </>
  );
};

export default Layout;
